// FILE: src/components/admin/TestimonialsManager.js
//
// Admin console — Testimonials manager (migration 078).
//
// List + quick filters (active / inactive / featured / missing consent /
// per-cert), add/edit form with a LIVE preview rendered by the same
// TestimonialCard the public carousel uses, up/down + numeric reordering
// persisted to Supabase, activate/deactivate, and confirmed hard delete.
//
// Eligibility rule surfaced everywhere: a row is publicly visible ONLY when
// is_active AND consent_confirmed (enforced server-side by the
// public_testimonials RPC; the UI mirrors it with a "Live" chip and a
// consent warning). Follows BadgeManager conventions: inline style object,
// busy/error banners, window.confirm for destructive actions, service-layer
// writes gated by admin RLS.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAllExams } from '../../config/exams';
import {
  listTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
  swapTestimonialOrder, uploadTestimonialImage, validateTestimonialFields,
  isPubliclyEligible, LIMITS,
} from '../../services/testimonialsService';
import TestimonialCard from '../testimonials/TestimonialCard';

const EMPTY_FORM = {
  display_name: '', professional_title: '', organization: '',
  testimonial_text: '', exam_id: '', certification_date: '',
  profile_image_url: '', linkedin_url: '', verification_url: '',
  attribution_label: '', is_featured: false, is_active: false,
  display_order: 100, consent_confirmed: false,
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'featured', label: 'Featured' },
  { id: 'no-consent', label: 'Missing consent' },
];

const S = {
  page: { minHeight: '100vh', background: '#f4f5fa', fontFamily: '"Segoe UI", system-ui, sans-serif' },
  header: { background: '#1a1a2e', color: '#fff', padding: '14px 22px', display: 'flex', alignItems: 'center', gap: 14 },
  back: { background: 'rgba(255,255,255,0.08)', color: '#dcdcec', border: '1px solid #33334e', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 14 },
  h1: { margin: 0, fontSize: 18, fontWeight: 800 },
  body: { maxWidth: 1180, margin: '0 auto', padding: '22px 22px 80px' },
  bar: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 14 },
  chip: (on) => ({ border: '1px solid ' + (on ? '#1565c0' : '#ccd3e0'), background: on ? '#1565c0' : '#fff', color: on ? '#fff' : '#333', borderRadius: 999, padding: '5px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }),
  select: { padding: '6px 8px', borderRadius: 8, border: '1px solid #ccd3e0', fontSize: 13 },
  add: { marginLeft: 'auto', background: '#1565c0', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  err: { background: '#fdecea', color: '#b71c1c', border: '1px solid #f5c6c0', borderRadius: 8, padding: '9px 13px', marginBottom: 12, fontSize: 13.5 },
  warn: { background: '#fff8e1', color: '#8a6d00', border: '1px solid #ffe082', borderRadius: 8, padding: '9px 13px', margin: '8px 0', fontSize: 13.5 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(16,16,40,0.06)' },
  th: { textAlign: 'left', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: 1, color: '#777', padding: '10px 10px', borderBottom: '2px solid #eceef4', background: '#fafbfe' },
  td: { padding: '10px 10px', borderBottom: '1px solid #f0f2f7', fontSize: 13.5, color: '#26314a', verticalAlign: 'middle' },
  live: { background: '#e6f6ee', color: '#0c7a43', borderRadius: 999, padding: '2px 9px', fontSize: 11.5, fontWeight: 700 },
  off: { background: '#eef0f5', color: '#666', borderRadius: 999, padding: '2px 9px', fontSize: 11.5, fontWeight: 700 },
  needs: { background: '#fff3e0', color: '#a35b00', borderRadius: 999, padding: '2px 9px', fontSize: 11.5, fontWeight: 700 },
  act: { border: '1px solid #ccd3e0', background: '#fff', borderRadius: 7, padding: '4px 9px', fontSize: 12.5, cursor: 'pointer', marginRight: 5 },
  danger: { border: '1px solid #e6b0ab', background: '#fff', color: '#b71c1c', borderRadius: 7, padding: '4px 9px', fontSize: 12.5, cursor: 'pointer' },
  formWrap: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,420px)', gap: 20, alignItems: 'start', background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(16,16,40,0.06)', marginBottom: 20 },
  label: { display: 'block', fontSize: 12.5, fontWeight: 700, color: '#445', margin: '12px 0 4px' },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '1px solid #ccd3e0', fontSize: 13.5, fontFamily: 'inherit' },
  fieldErr: { color: '#b71c1c', fontSize: 12, marginTop: 3 },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  checks: { display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 14, fontSize: 13.5 },
  save: { background: '#0c7a43', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginRight: 8 },
  cancel: { background: '#fff', color: '#333', border: '1px solid #ccd3e0', borderRadius: 8, padding: '9px 18px', fontSize: 14, cursor: 'pointer' },
  // Matches the home page's .stories-col panel so the preview is faithful.
  previewBox: { background: 'linear-gradient(135deg, #1b2b46 0%, #22364f 55%, #2c4a46 100%)', border: '1px solid #24405f', borderRadius: 18, padding: 20 },
  previewTitle: { margin: '0 0 10px', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#9fb3c5', fontWeight: 700 },
};

export default function TestimonialsManager({ onBack }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');
  const [certFilter, setCertFilter] = useState('all');
  const [editing, setEditing] = useState(null);   // null | 'new' | row.id
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [previewRow, setPreviewRow] = useState(null);
  const exams = useMemo(() => getAllExams(), []);

  const reload = useCallback(async () => {
    try {
      setRows(await listTestimonials());
    } catch (e) {
      setError(e.message || String(e));
      setRows([]);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Wraps every mutation: busy flag, error banner, reload after success.
  const run = useCallback(async (fn) => {
    setBusy(true); setError('');
    try { await fn(); await reload(); return true; }
    catch (e) { setError(e.message || String(e)); return false; }
    finally { setBusy(false); }
  }, [reload]);

  const filtered = useMemo(() => {
    const list = rows || [];
    return list.filter((r) => {
      if (certFilter !== 'all' && r.exam_id !== certFilter) return false;
      if (filter === 'active') return r.is_active;
      if (filter === 'inactive') return !r.is_active;
      if (filter === 'featured') return r.is_featured;
      if (filter === 'no-consent') return !r.consent_confirmed;
      return true;
    });
  }, [rows, filter, certFilter]);

  const startCreate = () => {
    const maxOrder = (rows || []).reduce((m, r) => Math.max(m, r.display_order || 0), 0);
    setForm({ ...EMPTY_FORM, display_order: maxOrder + 10 });
    setFormErrors({}); setPreviewRow(null); setEditing('new');
  };
  const startEdit = (row) => {
    setForm({
      ...EMPTY_FORM,
      ...Object.fromEntries(
        Object.keys(EMPTY_FORM).map((k) => [k, row[k] == null ? EMPTY_FORM[k] : row[k]])
      ),
    });
    setFormErrors({}); setPreviewRow(null); setEditing(row.id);
  };
  const cancelEdit = () => { setEditing(null); setFormErrors({}); };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const saveForm = async () => {
    const { ok, errors } = validateTestimonialFields(form);
    setFormErrors(errors);
    if (!ok) return;
    const saved = await run(() =>
      editing === 'new' ? createTestimonial(form) : updateTestimonial(editing, form)
    );
    if (saved) setEditing(null);
  };

  const toggleActive = (row) =>
    run(() => updateTestimonial(row.id, { is_active: !row.is_active }));

  const move = (row, dir) => {
    // Swap display_order with the visual neighbour in the current full list
    // (sorted the way the public carousel sorts), persisting both rows.
    const list = rows || [];
    const idx = list.findIndex((r) => r.id === row.id);
    const other = list[idx + dir];
    if (!other) return;
    run(() => swapTestimonialOrder(row, other));
  };

  const remove = (row) => {
    // Soft-deactivation is preferred; hard delete demands confirmation.
    // eslint-disable-next-line no-alert
    const okGo = window.confirm(
      `Permanently delete the testimonial from "${row.display_name}"?\n\n` +
      'Prefer Deactivate if you may want it back — deletion cannot be undone. ' +
      'Any uploaded profile image stays in Storage for a later cleanup pass.'
    );
    if (okGo) run(() => deleteTestimonial(row.id));
  };

  const onUploadFile = async (file) => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const { publicUrl } = await uploadTestimonialImage(file);
      setField('profile_image_url', publicUrl);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');

  const input = (key, props = {}) => (
    <>
      <input
        style={S.input}
        data-testid={`t-field-${key}`}
        value={form[key] ?? ''}
        onChange={(e) => setField(key, e.target.value)}
        {...props}
      />
      {formErrors[key] && <div style={S.fieldErr} data-testid={`t-error-${key}`}>{formErrors[key]}</div>}
    </>
  );

  return (
    <div style={S.page} data-testid="testimonials-manager">
      <div style={S.header}>
        <button type="button" style={S.back} onClick={onBack} data-testid="testimonials-back">← Back</button>
        <h1 style={S.h1}>💬 Testimonials</h1>
        {busy && <span style={{ fontSize: 13, color: '#9a9ac0' }}>Working…</span>}
      </div>
      <div style={S.body}>
        {error && <div style={S.err} data-testid="testimonials-error">{error}</div>}

        {editing !== null && (
          <div style={S.formWrap} data-testid="testimonial-form">
            <div>
              <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>
                {editing === 'new' ? 'Add testimonial' : 'Edit testimonial'}
              </h2>
              <p style={{ margin: 0, fontSize: 12.5, color: '#667' }}>
                Enter testimonials exactly as approved by the learner. Consider
                first-name-only or an abbreviated surname. Never include email
                addresses. Certification claims are entered manually — nothing
                is derived from PM&nbsp;Pixel usage data.
              </p>

              <label style={S.label}>Display name * (max {LIMITS.displayName})</label>
              {input('display_name', { maxLength: LIMITS.displayName })}

              <label style={S.label}>Testimonial text * (max {LIMITS.testimonialText})</label>
              <textarea
                style={{ ...S.input, minHeight: 110, resize: 'vertical' }}
                data-testid="t-field-testimonial_text"
                maxLength={LIMITS.testimonialText}
                value={form.testimonial_text}
                onChange={(e) => setField('testimonial_text', e.target.value)}
              />
              {formErrors.testimonial_text && (
                <div style={S.fieldErr} data-testid="t-error-testimonial_text">{formErrors.testimonial_text}</div>
              )}

              <div style={S.row2}>
                <div>
                  <label style={S.label}>Professional title</label>
                  {input('professional_title', { maxLength: LIMITS.professionalTitle })}
                </div>
                <div>
                  <label style={S.label}>Organization</label>
                  {input('organization', { maxLength: LIMITS.organization })}
                </div>
              </div>

              <div style={S.row2}>
                <div>
                  <label style={S.label}>Certification *</label>
                  <select
                    style={S.input}
                    data-testid="t-field-exam_id"
                    value={form.exam_id}
                    onChange={(e) => setField('exam_id', e.target.value)}
                  >
                    <option value="">Select…</option>
                    {exams.map((e) => (
                      <option key={e.examId} value={e.examId}>{e.displayName}</option>
                    ))}
                  </select>
                  {formErrors.exam_id && <div style={S.fieldErr} data-testid="t-error-exam_id">{formErrors.exam_id}</div>}
                </div>
                <div>
                  <label style={S.label}>Certification date</label>
                  {input('certification_date', { type: 'date' })}
                </div>
              </div>

              <label style={S.label}>Profile image</label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                data-testid="t-field-image-file"
                onChange={(e) => onUploadFile(e.target.files && e.target.files[0])}
              />
              {form.profile_image_url && (
                <button
                  type="button"
                  style={{ ...S.act, marginLeft: 8 }}
                  data-testid="t-remove-image"
                  onClick={() => setField('profile_image_url', '')}
                >
                  Remove image
                </button>
              )}
              <div style={{ fontSize: 12, color: '#667', marginTop: 3 }}>
                PNG/JPG/WebP, max 2&nbsp;MB. Without a photo the card shows a
                neutral initials tile.
              </div>

              <div style={S.row2}>
                <div>
                  <label style={S.label}>LinkedIn URL</label>
                  {input('linkedin_url', { placeholder: 'https://…' })}
                </div>
                <div>
                  <label style={S.label}>Verification URL</label>
                  {input('verification_url', { placeholder: 'https://…' })}
                </div>
              </div>

              <div style={S.row2}>
                <div>
                  <label style={S.label}>Attribution label</label>
                  {input('attribution_label', { placeholder: 'e.g. Founding Tester', maxLength: LIMITS.attributionLabel })}
                </div>
                <div>
                  <label style={S.label}>Display order</label>
                  {input('display_order', {
                    type: 'number',
                    onChange: (e) => setField('display_order', Number(e.target.value)),
                  })}
                </div>
              </div>

              <div style={S.checks}>
                <label>
                  <input
                    type="checkbox"
                    data-testid="t-field-is_active"
                    checked={form.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                  /> Active
                </label>
                <label>
                  <input
                    type="checkbox"
                    data-testid="t-field-is_featured"
                    checked={form.is_featured}
                    onChange={(e) => setField('is_featured', e.target.checked)}
                  /> Featured
                </label>
                <label>
                  <input
                    type="checkbox"
                    data-testid="t-field-consent_confirmed"
                    checked={form.consent_confirmed}
                    onChange={(e) => setField('consent_confirmed', e.target.checked)}
                  /> Publication consent confirmed
                </label>
              </div>

              {form.is_active && !form.consent_confirmed && (
                <div style={S.warn} data-testid="consent-warning">
                  ⚠ This testimonial is marked Active but consent is not
                  confirmed — it will NOT appear publicly until consent is
                  recorded.
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <button type="button" style={S.save} onClick={saveForm} disabled={busy} data-testid="testimonial-save">
                  {editing === 'new' ? 'Create' : 'Save changes'}
                </button>
                <button type="button" style={S.cancel} onClick={cancelEdit} data-testid="testimonial-cancel">
                  Cancel
                </button>
              </div>
            </div>

            <div style={S.previewBox}>
              <p style={S.previewTitle}>Live preview — exactly as the home page renders it</p>
              <TestimonialCard testimonial={form} />
            </div>
          </div>
        )}

        <div style={S.bar}>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              style={S.chip(filter === f.id)}
              data-testid={`t-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
          <select
            style={S.select}
            data-testid="t-filter-cert"
            value={certFilter}
            onChange={(e) => setCertFilter(e.target.value)}
            aria-label="Filter by certification"
          >
            <option value="all">All certifications</option>
            {exams.map((e) => (
              <option key={e.examId} value={e.examId}>{e.displayName}</option>
            ))}
          </select>
          {editing === null && (
            <button type="button" style={S.add} onClick={startCreate} data-testid="testimonials-add">
              + Add testimonial
            </button>
          )}
        </div>

        {rows === null ? (
          <p>Loading…</p>
        ) : filtered.length === 0 ? (
          <p data-testid="testimonials-empty">No testimonials match this view.</p>
        ) : (
          <table style={S.table} data-testid="testimonials-table">
            <thead>
              <tr>
                {['', 'Name', 'Cert', 'Testimonial', 'Order', 'Status', 'Consent', 'Updated', 'Actions'].map((h, i) => (
                  <th key={i} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`testimonial-row-${r.id}`}>
                  <td style={S.td} title={r.profile_image_url ? 'Has profile photo' : 'No photo (initials fallback)'}>
                    {r.profile_image_url ? '🖼️' : '—'}
                  </td>
                  <td style={S.td}>
                    <strong>{r.display_name}</strong>
                    {r.is_featured && <span title="Featured"> ★</span>}
                  </td>
                  <td style={S.td}>{(exams.find((e) => e.examId === r.exam_id) || {}).displayName || r.exam_id}</td>
                  <td style={{ ...S.td, maxWidth: 260 }}>
                    {String(r.testimonial_text || '').slice(0, 70)}
                    {String(r.testimonial_text || '').length > 70 ? '…' : ''}
                  </td>
                  <td style={S.td}>{r.display_order}</td>
                  <td style={S.td}>
                    {isPubliclyEligible(r)
                      ? <span style={S.live}>Live</span>
                      : r.is_active
                        ? <span style={S.needs}>Active, not eligible</span>
                        : <span style={S.off}>Inactive</span>}
                  </td>
                  <td style={S.td}>{r.consent_confirmed ? '✓' : <span style={S.needs}>Missing</span>}</td>
                  <td style={S.td}>{fmtDate(r.updated_at)}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button type="button" style={S.act} data-testid={`testimonial-up-${r.id}`} aria-label={`Move ${r.display_name} up`} onClick={() => move(r, -1)} disabled={busy}>↑</button>
                    <button type="button" style={S.act} data-testid={`testimonial-down-${r.id}`} aria-label={`Move ${r.display_name} down`} onClick={() => move(r, 1)} disabled={busy}>↓</button>
                    <button type="button" style={S.act} data-testid={`testimonial-edit-${r.id}`} onClick={() => startEdit(r)}>Edit</button>
                    <button type="button" style={S.act} data-testid={`testimonial-preview-${r.id}`} onClick={() => setPreviewRow(r)}>Preview</button>
                    <button type="button" style={S.act} data-testid={`testimonial-toggle-${r.id}`} onClick={() => toggleActive(r)} disabled={busy}>
                      {r.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button type="button" style={S.danger} data-testid={`testimonial-delete-${r.id}`} onClick={() => remove(r)} disabled={busy}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {previewRow && (
          <div style={{ ...S.previewBox, marginTop: 20, maxWidth: 480 }} data-testid="testimonial-preview-panel">
            <p style={S.previewTitle}>
              Preview — {previewRow.display_name}{' '}
              <button type="button" style={S.act} data-testid="testimonial-preview-close" onClick={() => setPreviewRow(null)}>Close</button>
            </p>
            <TestimonialCard testimonial={previewRow} />
          </div>
        )}
      </div>
    </div>
  );
}
